import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getCurrentLegalDocuments } from "../api/legal";
import AppLogo from "../components/AppLogo";
import { getApiError } from "../api/errors";

export default function LegalDocument({ documentType }) {
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    getCurrentLegalDocuments()
      .then((response) => {
        if (!active) return;
        setDocument(response.data?.[documentType] || null);
        setMeta(response.data || null);
      })
      .catch((requestError) => {
        if (active) setError(getApiError(requestError, "Could not load this legal document."));
      });
    return () => {
      active = false;
    };
  }, [documentType]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc" }}>
      <Box component="header" sx={{ bgcolor: "#071523", borderBottom: "1px solid rgba(148,163,184,.16)" }}>
        <Container maxWidth="lg" sx={{ py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <AppLogo light />
            <Stack direction="row" spacing={1}>
              <Button
                color="inherit"
                startIcon={<ArrowBackRoundedIcon />}
                onClick={() => navigate(-1)}
                sx={{ color: "#cbd5e1" }}
              >
                Back
              </Button>
              <Button component={RouterLink} to="/login" variant="outlined" sx={{ color: "white", borderColor: "rgba(255,255,255,.25)" }}>
                Sign in
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 7 } }}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : !document ? (
          <Box sx={{ minHeight: 340, display: "grid", placeItems: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ p: { xs: 2.5, sm: 4, md: 5 }, borderRadius: 4, border: "1px solid #e2e8f0" }} elevation={0}>
            <Typography variant="overline" color="primary" fontWeight={900} letterSpacing=".1em">
              ABOS LEGAL
            </Typography>
            <Typography variant="h3" sx={{ mt: 0.8, fontSize: { xs: "2rem", md: "2.65rem" } }}>
              {document.title}
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Effective {document.effective_date} · Version {document.version}
            </Typography>
            <Divider sx={{ my: 3.5 }} />

            <Stack spacing={3.4}>
              {document.sections?.map((section) => (
                <Box key={section.heading}>
                  <Typography variant="h6" fontWeight={900}>
                    {section.heading}
                  </Typography>
                  <Stack spacing={1.25} sx={{ mt: 1.1 }}>
                    {section.paragraphs?.map((paragraph, index) => (
                      <Typography key={`${section.heading}-${index}`} color="text.secondary" sx={{ lineHeight: 1.8 }}>
                        {paragraph}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>

            <Divider sx={{ my: 4 }} />
            <Typography variant="body2" color="text.secondary">
              Contact: {meta?.contact_email || "aibossupport@gmail.com"} · Governing law: {meta?.governing_law || "Lebanon"}
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
}
